/**
 * PLY (Polygon File Format) Exporter
 * Exports Three.js geometry/mesh to PLY format with optional color preservation
 */

import * as THREE from 'three';

/**
 * Convert Three.js geometry to PLY format (ASCII)
 * Supports vertex colors if available
 * @param {THREE.Geometry|THREE.BufferGeometry} geometry - The geometry to export
 * @param {Object} options - Export options { preserveColors: boolean }
 * @returns {string} PLY data as text
 */
export function geometryToPLY(geometry, options = {}) {
  const { preserveColors = true } = options;

  // Ensure we have a BufferGeometry
  let bufferGeometry = geometry;
  if (!(geometry instanceof THREE.BufferGeometry)) {
    bufferGeometry = new THREE.BufferGeometry().fromGeometry(geometry);
  }

  // Create a copy to avoid modifying the original
  bufferGeometry = bufferGeometry.clone();

  const positions = bufferGeometry.getAttribute('position');
  const normals = bufferGeometry.getAttribute('normal');
  const colors = preserveColors ? bufferGeometry.getAttribute('color') : null;
  const indices = bufferGeometry.getIndex();

  if (!positions) {
    throw new Error('Geometry must have position attribute');
  }

  // Calculate vertex and face counts
  const vertexCount = positions.count;
  let faceCount = 0;
  if (indices) {
    faceCount = indices.count / 3;
  } else {
    faceCount = positions.count / 3;
  }

  // Determine if we have colors
  const hasColors = colors !== null && colors !== undefined;

  // Start building PLY file
  let plyData = 'ply\n';
  plyData += 'format ascii 1.0\n';
  plyData += `comment Three.js PLY Export\n`;
  plyData += `element vertex ${vertexCount}\n`;
  plyData += 'property float x\n';
  plyData += 'property float y\n';
  plyData += 'property float z\n';

  if (normals) {
    plyData += 'property float nx\n';
    plyData += 'property float ny\n';
    plyData += 'property float nz\n';
  }

  if (hasColors) {
    plyData += 'property uchar red\n';
    plyData += 'property uchar green\n';
    plyData += 'property uchar blue\n';
  }

  plyData += `element face ${faceCount}\n`;
  plyData += 'property list uchar int vertex_indices\n';
  plyData += 'end_header\n';

  // Write vertices
  const pos = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const color = new THREE.Color();

  for (let i = 0; i < vertexCount; i++) {
    pos.fromBufferAttribute(positions, i);
    plyData += `${pos.x} ${pos.y} ${pos.z}`;

    if (normals) {
      normal.fromBufferAttribute(normals, i);
      plyData += ` ${normal.x} ${normal.y} ${normal.z}`;
    }

    if (hasColors) {
      color.fromBufferAttribute(colors, i);
      const r = Math.round(color.r * 255);
      const g = Math.round(color.g * 255);
      const b = Math.round(color.b * 255);
      plyData += ` ${r} ${g} ${b}`;
    }

    plyData += '\n';
  }

  // Write faces
  for (let i = 0; i < faceCount; i++) {
    const a = indices ? indices.getX(i * 3) : i * 3;
    const b = indices ? indices.getX(i * 3 + 1) : i * 3 + 1;
    const c = indices ? indices.getX(i * 3 + 2) : i * 3 + 2;

    plyData += `3 ${a} ${b} ${c}\n`;
  }

  return plyData;
}

/**
 * Export Three.js mesh to PLY format
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {Object} options - Export options
 * @returns {string} PLY data as text
 */
export function meshToPLY(mesh, options = {}) {
  if (!mesh || !mesh.geometry) {
    throw new Error('Invalid mesh provided');
  }

  return geometryToPLY(mesh.geometry, options);
}

/**
 * Create Blob from PLY data
 * @param {string} plyData - PLY text data
 * @returns {Blob} PLY blob
 */
export function createPLYBlob(plyData) {
  return new Blob([plyData], { type: 'text/plain;charset=utf-8' });
}

/**
 * Complete export function
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {string} filename - Filename for download
 * @param {Object} options - Export options { preserveColors: boolean }
 */
export function exportMeshToPLY(mesh, filename, options = {}) {
  try {
    const plyData = meshToPLY(mesh, options);
    const blob = createPLYBlob(plyData);
    downloadBlob(blob, filename);
  } catch (error) {
    console.error('Error exporting to PLY:', error);
    throw error;
  }
}

/**
 * Helper function to trigger file download
 * @param {Blob} blob - Data blob
 * @param {string} filename - Filename
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
